import { SafeFreshchatWrapper } from './freshchat-wrapper';
import type { FreshchatUserConfig, WrapperState } from '../types/freshchat';
import { WrapperStates } from '../types/freshchat';
import { getFreshchatConfig } from './freshchat-config';

export class FreshchatService {
  public reCheckTimer = 1000;
  public wrapper: SafeFreshchatWrapper | null = null;
  public wrapperState: WrapperState = WrapperStates.Down;
  private lastUpdate: string = '';
  private scriptLoaded = false;

  public updateFreshchat(userConfig: FreshchatUserConfig): void {
    const newUpdate = JSON.stringify(userConfig || {});
    if (this.lastUpdate === newUpdate) {
      return;
    }

    this.lastUpdate = newUpdate;

    if (!this.wrapper) {
      this.bootFreshchat(userConfig);
    } else {
      this.updateCustomerData(userConfig);
    }
  }

  public async updateCustomerData(params: FreshchatUserConfig): Promise<void> {
    if (
      this.wrapperState !== WrapperStates.Booted ||
      !params ||
      !params.firstName ||
      !params.lastName
    ) {
      return;
    }

    if (this.wrapper) {
      await this.wrapper.freshchatUser('setProperties', params);

      // Set external ID if phone is provided
      if (params.externalId || params.phone) {
        await this.wrapper.setExternalId(
          params.externalId || params.phone || ''
        );
      }
    }
  }

  public bootFreshchat(cfg: FreshchatUserConfig): void {
    if (!this.wrapper) {
      this.appendFreshJSScript(cfg);
      this.wrapper = new SafeFreshchatWrapper(this.reCheckTimer);
    }
  }

  public appendFreshJSScript(cfg: FreshchatUserConfig): void {
    if (this.scriptLoaded) {
      this.initChat(cfg);
      return;
    }

    const doc = document;
    const freshEl = doc.createElement('script');

    // FreshChat CDN URL - replace with your widget URL if different
    const freshWidgetUrl = '//eu.fw-cdn.com/12681813/679668.js';

    const element = this.appendHtmlElement(freshEl, cfg, freshWidgetUrl);
    doc.head.appendChild(element);
    this.scriptLoaded = true;
  }

  public appendHtmlElement(
    freshEl: HTMLScriptElement,
    cfg: FreshchatUserConfig,
    freshWidgetUrl: string
  ): HTMLScriptElement {
    freshEl.src = freshWidgetUrl;
    freshEl.async = true;

    freshEl.onload = () => this.initChat(cfg);

    return freshEl;
  }

  public async initChat(cfg: FreshchatUserConfig): Promise<void> {
    this.wrapperState = WrapperStates.Booted;

    if (!this.wrapper) {
      this.wrapper = new SafeFreshchatWrapper(this.reCheckTimer);
    }

    const bootConfig = getFreshchatConfig();

    const initConf = {
      token: bootConfig.token,
      host: bootConfig.hostUrl,
      config: {
        headerProperty: {
          hideChatButton: bootConfig.isWidgetHidden ?? false,
        },
      },
    };

    await this.wrapper.freshchat('init', initConf);
    await this.wrapper.freshchat('config', initConf.config);

    // Update customer data after initialization
    // This approach is used because there's a bug with adding user in init method
    await this.updateCustomerData(cfg);
  }

  public async updateUrl(newUrl: string): Promise<void> {
    if (this.wrapperState !== WrapperStates.Booted || !newUrl) {
      return;
    }

    if (this.wrapper) {
      await this.wrapper.freshchat('trackPage', {
        url: newUrl,
      });
    }
  }

  public routeChanged(newRoute: string): void {
    this.updateUrl(newRoute);
  }

  public isLoaded(): boolean {
    return this.wrapper?.isLoaded() ?? false;
  }

  public isInitialized(): boolean {
    return this.wrapper?.isInitialized() ?? false;
  }

  public reboot(cfg: FreshchatUserConfig): void {
    if (this.wrapperState !== WrapperStates.Down) {
      this.stop();
    }

    this.bootFreshchat(cfg);
  }

  public async showWidget(): Promise<void> {
    if (this.wrapperState !== WrapperStates.Booted) {
      return;
    }

    if (this.wrapper) {
      await this.wrapper.freshchat('open');
    }
  }

  public async start(cfg?: FreshchatUserConfig): Promise<void> {
    if (this.wrapperState !== WrapperStates.Down) {
      return;
    }

    this.wrapperState = WrapperStates.Booted;
    this.bootFreshchat(cfg || {});
  }

  public async stop(): Promise<void> {
    if (this.wrapperState === WrapperStates.Down) {
      return;
    }

    this.wrapperState = WrapperStates.Down;

    if (this.wrapper) {
      await this.wrapper.freshchat('destroy');
      this.wrapper = null;
    }

    this.scriptLoaded = false;
  }
}

export const freshchatService = new FreshchatService();
